/**
 * State Ops hooks (ENR-037, ENR-038).
 *
 * Wraps the workflow/cases State Ops surfaces:
 *   - GET  /api/v1/state-trackers                      → StateTracker[] (weekly tracker)
 *   - GET  /api/v1/state-responses                     → StateResponse[] (response inbox)
 *   - POST /api/v1/state-responses/{id}:classify        → StateResponse (structured intake)
 *
 * Both lists tolerate either a bare array or an `{ items: [...] }` envelope (the
 * two conventions in this codebase) and degrade gracefully on 404 so the page
 * shows a friendly empty state when the sibling backend hasn't wired them yet.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';

/** One row of the auto-generated weekly state submission tracker. */
export interface StateTracker {
  id: string;
  case_id: string;
  /** State program / target the application was routed to. */
  state_program: string;
  /** `submitted` | `pending` | `in_review` | `approved` | `denied` | `info_requested`. */
  status: string;
  submitted_at?: string | null;
  /** SLA / expected-decision date. */
  due_at?: string | null;
  receipt_id?: string | null;
}

/** An inbound state response awaiting structured intake/classification. */
export interface StateResponse {
  id: string;
  case_id: string;
  state_program?: string | null;
  /** Raw / summarized response text. */
  summary?: string | null;
  /** `unclassified` | `approval` | `denial` | `info_request` | `other`. */
  classification: string;
  received_at: string;
  attachment_ref?: string | null;
}

export interface ClassifyResponseBody {
  /** Target classification, e.g. `approval` | `denial` | `info_request`. */
  classification: string;
  note?: string;
}

function unwrap<T>(data: T[] | { items?: T[] } | null | undefined): T[] {
  if (Array.isArray(data)) return data;
  return data?.items ?? [];
}

interface UseStateOpsResult {
  trackers: StateTracker[];
  responses: StateResponse[];
  trackersAvailable: boolean;
  responsesAvailable: boolean;
  isLoading: boolean;
  isWorking: boolean;
  error: ApiError | null;
  actionError: ApiError | null;
  refresh: () => void;
  classifyResponse: (id: string, body: ClassifyResponseBody) => Promise<boolean>;
}

export function useStateOps(): UseStateOpsResult {
  const [trackers, setTrackers] = useState<StateTracker[]>([]);
  const [responses, setResponses] = useState<StateResponse[]>([]);
  const [trackersAvailable, setTrackersAvailable] = useState(true);
  const [responsesAvailable, setResponsesAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);
    const trackersP = apiClient
      .get<StateTracker[] | { items: StateTracker[] }>('/state-trackers')
      .then((data) => {
        setTrackers(unwrap(data));
        setTrackersAvailable(true);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setTrackersAvailable(false);
          setTrackers([]);
          return;
        }
        throw err;
      });
    const responsesP = apiClient
      .get<StateResponse[] | { items: StateResponse[] }>('/state-responses')
      .then((data) => {
        setResponses(unwrap(data));
        setResponsesAvailable(true);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setResponsesAvailable(false);
          setResponses([]);
          return;
        }
        throw err;
      });
    Promise.allSettled([trackersP, responsesP])
      .then((results) => {
        const failure = results.find(
          (r) => r.status === 'rejected'
        ) as PromiseRejectedResult | undefined;
        if (failure) {
          const reason = failure.reason;
          setError(reason instanceof ApiError ? reason : new ApiError(0, 'ERROR', String(reason)));
        } else {
          setError(null);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const classifyResponse = useCallback(
    async (id: string, body: ClassifyResponseBody): Promise<boolean> => {
      setIsWorking(true);
      setActionError(null);
      try {
        const updated = await apiClient.post<StateResponse>(
          `/state-responses/${encodeURIComponent(id)}:classify`,
          body
        );
        setResponses((rs) =>
          rs.map((r) =>
            r.id === id
              ? { ...r, classification: updated?.classification ?? body.classification }
              : r
          )
        );
        return true;
      } catch (err: unknown) {
        setActionError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        return false;
      } finally {
        setIsWorking(false);
      }
    },
    []
  );

  return {
    trackers,
    responses,
    trackersAvailable,
    responsesAvailable,
    isLoading,
    isWorking,
    error,
    actionError,
    refresh,
    classifyResponse,
  };
}
